import { TsDOM as q } from '@/scripts/core/TsDOM'
import { type CartItems } from '@/scripts/cart/cart-items'
import { type CartDrawerItems } from '@/scripts/cart/cart-drawer-items'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import {
	configPostRequest,
	getCart,
	getPersonalizationCartComponents,
} from '@/scripts/core/cart-functions'

export class CartRemoveButton extends UcoastEl {
	static htmlSelector = 'cart-remove-button'
	constructor() {
		super()

		this.addEventListener('click', this.removeItem.bind(this))
	}

	removeItem() {
		const cartItems =
			q.oc<CartItems>(this, 'cart-items') ||
			q.rc<CartDrawerItems>(this, 'cart-drawer-items')
		const line = q.ra(this, 'data-index')
		const groupItemKeys = q.oa(this, 'data-group-item-keys')
		const personalizationGroupId = q.oa(
			this,
			'data-personalization-group-id'
		)

		if (personalizationGroupId) {
			void this.removePersonalization(
				cartItems,
				line,
				personalizationGroupId
			)
			return
		}

		if (groupItemKeys) {
			const updates = groupItemKeys
				.split(',')
				.map((key) => key.trim())
				.filter(Boolean)
				.reduce<Record<string, number>>((acc, key) => {
					acc[key] = 0
					return acc
				}, {})

			cartItems.updateItemsByKey(line, updates)
			return
		}

		cartItems.updateQuantity(line, '0')
	}

	private async removePersonalization(
		cartItems: CartItems | CartDrawerItems,
		line: string,
		groupId: string
	) {
		try {
			const [cart, components] = await Promise.all([
				getCart(),
				getPersonalizationCartComponents(),
			])
			if (!('items' in cart)) throw new Error(cart.description)
			const component = components.find(
				(item) => item.groupId === groupId
			)
			const parent = cart.items.find(
				(item) => item.key === component?.parentKey
			)
			if (!component || !parent) throw new Error(window.cartStrings.error)

			if (component.childKey) {
				const removeResponse = await fetch(
					window.routes.cart_change_url,
					configPostRequest({ id: component.childKey, quantity: 0 })
				).then((response) => response.json())
				if (removeResponse.status)
					throw new Error(removeResponse.description)
			}

			const properties = {
				...Object.fromEntries(
					Object.entries(parent.properties ?? {}).filter(
						([key]) =>
							!key.startsWith('_personalization_') &&
							key !== '_customization_group_id'
					)
				),
				_plain_pair_id: groupId,
			}
			const sections = cartItems
				.getSectionsToRender()
				.map((section) => section.section)
			const parentResponse = await fetch(
				window.routes.cart_change_url,
				configPostRequest({
					id: parent.key,
					quantity: parent.quantity,
					properties,
					sections,
					sections_url: window.location.pathname,
				})
			).then((response) => response.json())
			if (parentResponse.status)
				throw new Error(parentResponse.description)
			cartItems.renderParsedState(parentResponse, {
				line,
				itemsCount: document.querySelectorAll(
					cartItems.instanceSelectors.item
				).length,
			})
		} catch (error) {
			console.error(error)
			const errors = q.os<HTMLElement>(cartItems.instanceSelectors.errors)
			if (errors) errors.textContent = window.cartStrings.error
		}
	}
}
